import {
  Accordion,
  Alert,
  Badge,
  Button,
  Flex,
  Group,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useMemo, useRef, useState } from 'react';
import StyledModal from '@/components/StyledModal/StyledModal.tsx';
import useRetrieveAutomationVersions from '@/hooks/api/automationVersions/useRetrieveAutomationVersions.ts';
import useRetrieveVersionComparisonV3 from '@/hooks/api/automationVersions/useRetrieveVersionComparisonV3.ts';
import useRetrieveVersionComparisonV3Detail from '@/hooks/api/automationVersions/useRetrieveVersionComparisonV3Detail.ts';
import useGenerateVersionReviewSummary from '@/hooks/api/automationVersions/useGenerateVersionReviewSummary.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import {
  AutomationVersionSimple,
  AutomationVersionReviewSummary,
  VersionComparisonV3AffectedItem,
  VersionComparisonV3DetailBlock,
  VersionComparisonV3Subject,
  getAutomationVersionDisplayName,
} from '@/types/automations.ts';
import { flattenInfiniteQueryData } from '@/utils/misc.ts';

type Props = {
  opened: boolean;
  onClose: () => void;
  publishedVersion?: AutomationVersionSimple | null;
  latestVersion?: AutomationVersionSimple | null;
  initialBaseVersionId?: string;
  initialTargetVersionId?: string;
  reviewSession?: number;
  onReviewReady?: (review: { targetFingerprint: string; baseVersionId: string }) => void;
};

const facetCopy: Record<VersionComparisonV3Subject['facets'][number]['kind'], string> = {
  added: 'Added',
  removed: 'Removed',
  renamed: 'Renamed',
  moved: 'Moved',
  behavior_changed: 'Behavior',
  input_binding_changed: 'Inputs',
  output_contract_changed: 'Outputs',
  integration_action_changed: 'Integration action',
  integration_setting_changed: 'Integration settings',
  schema_changed: 'Schema',
  model_setting_changed: 'Model settings',
  test_assurance_changed: 'Test assurance',
};

const facetColor: Record<VersionComparisonV3Subject['facets'][number]['kind'], string> = {
  added: 'green',
  removed: 'red',
  renamed: 'violet',
  moved: 'orange',
  behavior_changed: 'blue',
  input_binding_changed: 'cyan',
  output_contract_changed: 'teal',
  integration_action_changed: 'indigo',
  integration_setting_changed: 'indigo',
  schema_changed: 'grape',
  model_setting_changed: 'pink',
  test_assurance_changed: 'yellow',
};

const relationshipCopy = (relationships: readonly string[]) => {
  const labels = [
    relationships.includes('data_input') && 'Uses this as data input',
    relationships.includes('validation_block') && 'Depends on this validation',
    relationships.includes('control_wait') && 'Waits on this step',
  ].filter(Boolean);
  return labels.length ? labels.join(' · ') : 'Directly connected';
};

const blockValue = (block: VersionComparisonV3DetailBlock, side: 'before' | 'after') =>
  block[side] ?? (block.kind === 'secret_changed' ? 'Changed' : 'Not set');

type VisibleDetailKind = Exclude<VersionComparisonV3DetailBlock['kind'], 'secret_changed' | 'unavailable'>;

const detailKindCopy: Record<VisibleDetailKind, string> = {
  binding_change: 'Connection updated',
  code_diff: 'Code updated',
  collection_change: 'List updated',
  scalar_change: 'Setting updated',
  schema_change: 'Schema updated',
  selection_change: 'Selection updated',
  text_diff: 'Text updated',
};

const detailChangeLabel = (block: VersionComparisonV3DetailBlock) => {
  if (block.before != null && block.after == null) return 'Removed';
  if (block.before == null && block.after != null) return 'Added';
  return detailKindCopy[block.kind as VisibleDetailKind];
};

const changedTextPreview = (beforeValue: string | null, afterValue: string | null) => {
  const before = Array.from(beforeValue ?? '');
  const after = Array.from(afterValue ?? '');
  let sharedStart = 0;
  while (sharedStart < Math.min(before.length, after.length) && before[sharedStart] === after[sharedStart]) {
    sharedStart += 1;
  }
  let sharedEnd = 0;
  while (
    sharedEnd < Math.min(before.length, after.length) - sharedStart &&
    before[before.length - sharedEnd - 1] === after[after.length - sharedEnd - 1]
  ) {
    sharedEnd += 1;
  }
  const excerpt = (value: string[]) => {
    const changeEnd = value.length - sharedEnd;
    const start = Math.max(0, sharedStart - 64);
    const visibleChangeEnd = Math.min(changeEnd, sharedStart + 240);
    const end = visibleChangeEnd < changeEnd ? visibleChangeEnd : Math.min(value.length, changeEnd + 64);
    return {
      leading: value.slice(start, sharedStart).join(''),
      changed: value.slice(sharedStart, visibleChangeEnd).join(''),
      trailing: value.slice(visibleChangeEnd, end).join(''),
      leadingOmitted: start > 0,
      trailingOmitted: end < value.length,
    };
  };
  return { before: excerpt(before), after: excerpt(after) };
};

function DetailBlock({ block }: { block: VersionComparisonV3DetailBlock }) {
  const [expanded, setExpanded] = useState(false);
  if (block.kind === 'unavailable' || block.kind === 'secret_changed') {
    return (
      <Paper withBorder p="sm">
        <Text size="sm" fw={600}>{block.label}</Text>
        <Text size="sm" c="dimmed">
          {block.summary || (block.kind === 'secret_changed' ? 'Changed' : 'Detailed review evidence is unavailable.')}
        </Text>
      </Paper>
    );
  }
  const isLongText = block.kind === 'text_diff' || block.kind === 'code_diff';
  const isCode = block.kind === 'code_diff';
  const hasValues = block.before != null || block.after != null;
  const needsDisclosure = block.label === 'Full flow definition' ||
    Math.max(block.before?.length || 0, block.after?.length || 0) > 600;
  const preview = needsDisclosure && block.kind === 'text_diff' && block.label !== 'Full flow definition'
    ? changedTextPreview(block.before, block.after)
    : null;
  return (
    <Paper withBorder p="sm">
      <Group justify="space-between" align="baseline" gap="xs">
        <Text size="sm" fw={600}>{block.label}</Text>
        <Text size="xs" c="dimmed">
          {detailChangeLabel(block)}
        </Text>
      </Group>
      {block.summary && !hasValues && <Text size="sm" c="dimmed" mt="xs">{block.summary}</Text>}
      {preview && !expanded && (
        <Flex gap="sm" direction={{ base: 'column', sm: 'row' }} mt="xs">
          {(['before', 'after'] as const).map((side) => {
            const excerpt = preview[side];
            return (
              <Stack key={side} gap={2} style={{ flex: 1, minWidth: 0 }}>
                <Text size="xs" c="dimmed" fw={600}>{side === 'before' ? 'Before · changed passage' : 'After · changed passage'}</Text>
                <Text component="div" size="sm" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {block[side] == null ? 'Not set' : (
                    <>
                      {excerpt.leadingOmitted && '…'}
                      {excerpt.leading}
                      {excerpt.changed && <mark style={{ background: 'var(--mantine-color-yellow-2)' }}>{excerpt.changed}</mark>}
                      {excerpt.trailing}
                      {excerpt.trailingOmitted && '…'}
                    </>
                  )}
                </Text>
              </Stack>
            );
          })}
        </Flex>
      )}
      {needsDisclosure && (
        <Button variant="subtle" color="brandBlue.6" size="xs" px={0} mt="xs" aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Hide full value' : 'Show full value'}
        </Button>
      )}
      {(!needsDisclosure || expanded) && hasValues && (
        <Flex gap="sm" direction={{ base: 'column', sm: 'row' }} mt="xs">
          {(['before', 'after'] as const).map((side) => (
            <Stack key={side} gap={2} style={{ flex: 1, minWidth: 0 }}>
              <Text size="xs" c="dimmed" fw={600}>{side === 'before' ? 'Before' : 'After'}</Text>
              <Text
                component={isCode ? 'pre' : 'div'}
                size="sm"
                fw={side === 'after' ? 600 : undefined}
                style={{ margin: 0, whiteSpace: isLongText ? 'pre-wrap' : 'normal', overflowWrap: 'anywhere' }}
              >
                {blockValue(block, side)}
              </Text>
            </Stack>
          ))}
        </Flex>
      )}
    </Paper>
  );
}

export default function VersionComparisonModal(props: Props) {
  const automationId = useAutomationId();
  const narrow = useMediaQuery('(max-width: 48em)');
  const history = useRetrieveAutomationVersions(false);
  const versions = useMemo<AutomationVersionSimple[]>(() => {
    const values = [
      ...flattenInfiniteQueryData(history.data),
      ...(props.publishedVersion ? [props.publishedVersion] : []),
      ...(props.latestVersion ? [props.latestVersion] : []),
    ];
    return [...new Map(values.map((version) => [version.id, version])).values()];
  }, [history.data, props.latestVersion, props.publishedVersion]);
  const defaultTarget = props.initialTargetVersionId || props.latestVersion?.id || versions[0]?.id;
  const defaultBaseCandidate = props.initialBaseVersionId || props.publishedVersion?.id || versions[1]?.id;
  const defaultBase = defaultBaseCandidate === defaultTarget && !props.initialBaseVersionId
    ? versions.find((version) => version.id !== defaultTarget)?.id
    : defaultBaseCandidate;
  const fixedPair = !!props.initialBaseVersionId && !!props.initialTargetVersionId;
  const [baseVersionId, setBaseVersionId] = useState<string | null>(defaultBase || null);
  const [targetVersionId, setTargetVersionId] = useState<string | null>(defaultTarget || null);
  const [selectedRef, setSelectedRef] = useState<string | null>(null);
  const [activeSummaryRequestKey, setActiveSummaryRequestKey] = useState<string | null>(null);
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const emitted = useRef<string | null>(null);
  const {
    data: reviewGuideData,
    error: reviewGuideError,
    isError: reviewGuideIsError,
    isPending: reviewGuideIsPending,
    mutate: generateReviewGuide,
    reset: resetReviewGuide,
  } = useGenerateVersionReviewSummary();

  useEffect(() => {
    if (!props.opened) return;
    setBaseVersionId(defaultBase || null);
    setTargetVersionId(defaultTarget || null);
  }, [defaultBase, defaultTarget, props.opened]);

  const validPair = !!baseVersionId && !!targetVersionId && baseVersionId !== targetVersionId;
  const comparison = useRetrieveVersionComparisonV3(
    baseVersionId || undefined,
    targetVersionId || undefined,
    props.opened && validPair,
    props.reviewSession,
  );
  const subjects = useMemo(() => comparison.data?.subjects || [], [comparison.data?.subjects]);
  const hasUnclassifiedChanges = subjects.some((subject) => subject.subject_kind === 'unclassified');

  useEffect(() => {
    setSelectedRef((current) =>
      current && subjects.some((subject) => subject.review_subject_ref === current)
        ? current
        : subjects[0]?.review_subject_ref || null,
    );
  }, [subjects]);

  const selected = subjects.find((subject) => subject.review_subject_ref === selectedRef);
  const connectedItems = useMemo<VersionComparisonV3AffectedItem[]>(
    () => selected?.affected_items || [],
    [selected],
  );
  const detail = useRetrieveVersionComparisonV3Detail(
    baseVersionId || undefined,
    targetVersionId || undefined,
    selected?.review_subject_ref,
    props.opened && !!selected,
    comparison.data?.target_fingerprint,
  );
  const matchingDetail =
    detail.data &&
    detail.data.target_fingerprint === comparison.data?.target_fingerprint &&
    detail.data.review_subject_ref === selected?.review_subject_ref
      ? detail.data
      : undefined;
  const businessOutcomeFields = selected?.subject_kind === 'business_outcome_assessment' &&
    matchingDetail?.detail_blocks.length &&
    matchingDetail.detail_blocks.every((block) => block.kind !== 'unavailable' && block.kind !== 'secret_changed')
    ? matchingDetail.detail_blocks : null;
  const selectedDescription = businessOutcomeFields && businessOutcomeFields.every((block) => block.after == null) &&
    businessOutcomeFields.some((block) => block.before != null)
    ? 'Business outcome assessment removed.'
    : businessOutcomeFields && businessOutcomeFields.every((block) => block.before == null) &&
      businessOutcomeFields.some((block) => block.after != null)
      ? 'Business outcome assessment added.'
      : selected?.facets.map((facet) => facet.summary).join(' ');
  const openCurrentDraftHref = matchingDetail?.navigation_step_id
    ? `/automations/${encodeURIComponent(automationId)}/editor?step=${encodeURIComponent(matchingDetail.navigation_step_id)}`
    : undefined;
  const reviewGuideKey = comparison.data && baseVersionId && targetVersionId
    ? `${baseVersionId}:${targetVersionId}:${comparison.data.target_fingerprint}`
    : null;
  const matchingReviewGuide: AutomationVersionReviewSummary | undefined =
    reviewGuideData &&
    reviewGuideData.base_version_id === baseVersionId &&
    reviewGuideData.target_version_id === targetVersionId &&
    reviewGuideData.target_fingerprint === comparison.data?.target_fingerprint
      ? reviewGuideData
      : undefined;
  const reviewGuideIsStale = reviewGuideError?.response?.data?.code === 'automation_review_stale';
  useEffect(() => {
    if (!props.opened || comparison.isFetching || !reviewGuideKey || !baseVersionId || !targetVersionId || !comparison.data || !subjects.length) {
      return;
    }
    if (activeSummaryRequestKey === reviewGuideKey || matchingReviewGuide) return;
    setActiveSummaryRequestKey(reviewGuideKey);
    generateReviewGuide({
      targetVersionId,
      baseVersionId,
      expectedTargetFingerprint: comparison.data.target_fingerprint,
    });
  }, [activeSummaryRequestKey, baseVersionId, comparison.data, comparison.isFetching, generateReviewGuide, matchingReviewGuide, props.opened, reviewGuideKey, subjects.length, targetVersionId]);

  useEffect(() => {
    if (!props.opened) {
      setActiveSummaryRequestKey(null);
      resetReviewGuide();
    }
  }, [props.opened, resetReviewGuide]);

  useEffect(() => {
    const key = `${props.reviewSession}:${comparison.data?.target_fingerprint}`;
    if (!comparison.data || comparison.isFetching || !baseVersionId || emitted.current === key) return;
    emitted.current = key;
    props.onReviewReady?.({ targetFingerprint: comparison.data.target_fingerprint, baseVersionId });
  }, [baseVersionId, comparison.data, comparison.isFetching, props]);

  const choose = (ref: string, focus = false) => {
    setSelectedRef(ref);
    if (focus) window.setTimeout(() => itemRefs.current[ref]?.focus(), 0);
  };
  const moveSelection = (offset: number) => {
    const current = subjects.findIndex((subject) => subject.review_subject_ref === selectedRef);
    const next = subjects[Math.max(0, Math.min(subjects.length - 1, current + offset))];
    if (next) choose(next.review_subject_ref, true);
  };

  const subjectList = (
    <ScrollArea
      aria-label="Review subjects"
      w={narrow ? '100%' : 336}
      h={narrow ? 180 : 340}
      p="sm"
      type="always"
      style={{
        flexShrink: 0,
        borderRight: narrow ? undefined : '1px solid #ECECED',
        borderBottom: narrow ? '1px solid #ECECED' : undefined,
      }}
    >
      <Stack gap={4}>
        {subjects.map((subject) => {
          const primaryFacet = subject.facets[0]?.kind || 'behavior_changed';
          return (
            <button
              key={subject.review_subject_ref}
              ref={(element) => { itemRefs.current[subject.review_subject_ref] = element; }}
              aria-label={`Review ${subject.subject_label}`}
              aria-current={selectedRef === subject.review_subject_ref ? 'true' : undefined}
              onClick={() => choose(subject.review_subject_ref)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') { event.preventDefault(); moveSelection(1); }
                if (event.key === 'ArrowUp') { event.preventDefault(); moveSelection(-1); }
              }}
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                border: selectedRef === subject.review_subject_ref ? '1px solid #B9C4DD' : '1px solid transparent',
                background: selectedRef === subject.review_subject_ref ? '#F0F3FA' : 'transparent',
                borderRadius: 6,
                padding: '9px 10px',
              }}
            >
              <Group gap={7} wrap="nowrap">
                <Badge size="xs" color={facetColor[primaryFacet]} variant="light" style={{ flexShrink: 0 }}>
                  {facetCopy[primaryFacet]}
                </Badge>
                <Text size="sm" fw={selectedRef === subject.review_subject_ref ? 600 : 400} truncate>
                  {subject.subject_label}
                </Text>
              </Group>
              <Text size="xs" c="dimmed" mt={4}>
                {subject.review_subject_ref === selectedRef ? selectedDescription : subject.facets.map((facet) => facet.summary).join(' ')}
              </Text>
            </button>
          );
        })}
      </Stack>
    </ScrollArea>
  );

  return (
    <StyledModal
      opened={props.opened}
      onClose={props.onClose}
      title="Review changes"
      withCloseButton
      closeButtonProps={{ 'aria-label': 'Close review', style: { marginLeft: 'auto' } }}
      size="min(1120px, calc(100vw - 24px))"
      scrollType="none"
      bodyPadding={false}
    >
      <Stack gap={0} style={{ height: 'min(700px, calc(90dvh - 80px))', minHeight: 0 }}>
        {!fixedPair && (
          <Stack p="md" pb="sm" gap="xs">
            <Flex gap="sm" align={narrow ? 'stretch' : 'end'} direction={narrow ? 'column' : 'row'}>
              <Group grow align="end" style={{ flex: 1, minWidth: 0 }}>
                <Select label="Changes from" searchable data={versions.map((version) => ({ value: version.id, label: getAutomationVersionDisplayName(version) }))} value={baseVersionId} onChange={setBaseVersionId} />
                <Select label="Compare to" searchable data={versions.map((version) => ({ value: version.id, label: getAutomationVersionDisplayName(version) }))} value={targetVersionId} onChange={setTargetVersionId} />
              </Group>
              {history.hasNextPage && <Button variant="subtle" color="gray" size="xs" w="fit-content" loading={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>Load older versions</Button>}
            </Flex>
          </Stack>
        )}
        {!validPair && <Alert color="gray" title="Choose two different versions" m="md">Select a base version and a version to compare.</Alert>}
        {validPair && comparison.isPending && <Text size="sm" p="md">Comparing versions…</Text>}
        {comparison.isError && <Alert color="red" title="Comparison unavailable" m="md">We couldn’t compare these versions. Try again in a moment.</Alert>}
        {comparison.data && (
          <ScrollArea type="always" scrollbars="y" style={{ flex: 1, minHeight: 0 }}>
            {subjects.length > 0 ? (
              <Accordion
                key={`${baseVersionId}:${targetVersionId}:${comparison.data.target_fingerprint}:${props.opened}`}
                multiple
                defaultValue={['summary', 'changes']}
                styles={{ content: { padding: 0 } }}
              >
                <Accordion.Item value="summary">
                  <Accordion.Control><Text size="sm" fw={600}>AI change summary</Text></Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="xs" p="md">
                      {reviewGuideIsPending && activeSummaryRequestKey === reviewGuideKey && !matchingReviewGuide && <Text size="sm" c="dimmed">Summarizing changes…</Text>}
                      {matchingReviewGuide && (
                        <>
                          <Text size="sm">{matchingReviewGuide.overview}</Text>
                          {matchingReviewGuide.highlights.map((highlight, index) => (
                            <Text key={`${highlight.review_subject_refs.join(':')}-${index}`} size="sm" pl="sm" style={{ borderLeft: '2px solid var(--mantine-color-gray-4)' }}>
                              {highlight.explanation}
                            </Text>
                          ))}
                          {matchingReviewGuide.omitted_subject_count > 0 && (
                            <Text size="xs" c="dimmed">
                              Summary covers {subjects.length - matchingReviewGuide.omitted_subject_count} of {subjects.length} changed subjects. All available changes are listed below.
                            </Text>
                          )}
                        </>
                      )}
                      {hasUnclassifiedChanges && (
                        <Text size="xs" c="dimmed">
                          Some saved changes could not be fully classified or detailed. Review the available comparison below.
                        </Text>
                      )}
                      {reviewGuideIsError && activeSummaryRequestKey === reviewGuideKey && !matchingReviewGuide && (
                        <Group gap="xs">
                          <Text size="sm" c="dimmed">
                            {reviewGuideIsStale
                              ? 'The target version changed while the summary was being prepared. Refresh the comparison to generate a current summary.'
                              : 'The summary is unavailable. The detailed comparison is still ready to review.'}
                          </Text>
                          <Button
                            variant="subtle"
                            size="xs"
                            loading={reviewGuideIsStale && comparison.isFetching}
                            onClick={() => {
                              if (reviewGuideIsStale) {
                                void comparison.refetch();
                                return;
                              }
                              if (!baseVersionId || !targetVersionId || !comparison.data) return;
                              generateReviewGuide({
                                targetVersionId,
                                baseVersionId,
                                expectedTargetFingerprint: comparison.data.target_fingerprint,
                              });
                            }}
                          >
                            {reviewGuideIsStale ? 'Refresh comparison' : 'Try again'}
                          </Button>
                        </Group>
                      )}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
                <Accordion.Item value="changes">
                  <Accordion.Control>
                    <Group gap="xs">
                      <Text size="sm" fw={600}>All changes</Text>
                      <Text size="xs" c="dimmed">{subjects.length}</Text>
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Flex direction={narrow ? 'column' : 'row'} style={{ minWidth: 0 }}>
                      {narrow ? (
                        <Select
                          label="Choose a change"
                          searchable
                          data={subjects.map((subject, index) => ({
                            value: subject.review_subject_ref,
                            label: `${index + 1}. ${subject.subject_label}`,
                          }))}
                          value={selectedRef}
                          onChange={(ref) => { if (ref) choose(ref); }}
                          mx="md"
                          mt="sm"
                        />
                      ) : subjectList}
                      <Stack p={narrow ? 'md' : 'lg'} gap="md" style={{ flex: 1, minWidth: 0 }}>
                        {selected && (
                          <>
                            <Stack gap={5}>
                              <Group gap="xs">
                                {selected.facets.map((facet) => <Badge key={facet.kind} color={facetColor[facet.kind]} variant="light">{facetCopy[facet.kind]}</Badge>)}
                              </Group>
                              <Group justify="space-between" align="center" gap="xs" wrap="wrap">
                                <Text size="lg" fw={650}>{selected.subject_label}</Text>
                                {openCurrentDraftHref && (
                                  <Button
                                    component="a"
                                    href={openCurrentDraftHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    size="xs"
                                    variant="subtle"
                                    color="brandBlue.6"
                                    aria-label="Open current draft in editor"
                                  >
                                    Open in editor
                                  </Button>
                                )}
                              </Group>
                              {selected.previous_subject_label && <Text size="sm" c="dimmed">Previously: {selected.previous_subject_label}</Text>}
                              <Text size="sm">{selectedDescription}</Text>
                            </Stack>
                            {connectedItems.length > 0 && (
                              <Stack gap={6}>
                                <Text size="xs" fw={700} c="dimmed" tt="uppercase">Directly connected items</Text>
                                {connectedItems.map((item, index) => <Paper key={`${item.label}-${index}`} withBorder p="xs"><Text size="sm" fw={550}>{item.label}</Text><Text size="xs" c="dimmed">{relationshipCopy(item.relationships)}</Text></Paper>)}
                              </Stack>
                            )}
                            {detail.isPending && <Text size="sm" c="dimmed">Loading review detail…</Text>}
                            {detail.isError && <Alert color="red">Details are unavailable. This change still needs review.</Alert>}
                            {matchingDetail?.detail_blocks.length ? (
                              <Stack gap="xs">
                                <Text size="xs" fw={700} c="dimmed" tt="uppercase">What changed</Text>
                                {matchingDetail.detail_blocks.map((block, index) => (
                                  <DetailBlock
                                    key={`${matchingDetail.review_subject_ref}-${matchingDetail.target_fingerprint}-${block.label}-${index}`}
                                    block={block}
                                  />
                                ))}
                              </Stack>
                            ) : null}
                            {matchingDetail?.truncated && <Text size="xs" c="dimmed">Some reviewable detail is not shown.</Text>}
                            {!detail.isPending && !detail.isError && (!matchingDetail || !matchingDetail.detail_blocks.length) && !matchingDetail?.truncated && (
                              <Text size="sm" c="dimmed">{matchingDetail?.unavailable_reason || 'No additional safe detail is available for this change.'}</Text>
                            )}
                          </>
                        )}
                      </Stack>
                    </Flex>
                  </Accordion.Panel>
                </Accordion.Item>
              </Accordion>
            ) : (
              <Alert color="gray" title="No configuration changes" m="md">These versions have the same comparable configuration.</Alert>
            )}
          </ScrollArea>
        )}
      </Stack>
    </StyledModal>
  );
}
